import './QCViewer.css';
import { useEffect, useState} from 'react';
import { useParams } from "react-router-dom";
import QualityControlService from '../Services/QualityControlService';
import { GiHamburgerMenu } from "react-icons/gi";
import { IoClose } from "react-icons/io5";


import logo from '../refs/CornerstoneSymbol.svg';
import loginLogo from '../refs/logo.jpg';
import loginText from '../refs/login.png';

const helpURL = 'https://cornerstonedetentionproducts.box.com/s/06e2f6agp4az5ohs62m5lzkh6kg0s5wu';

const getProjectData = async (sessionId, projectId, setProjectData, setProjectStats, setFailure) => {
    QualityControlService.getProjectData(sessionId, projectId)
        .then((response) => {
            if(response.status > 199 && response.status < 300) {
                console.log(response.data);
                setProjectData(response.data.qcList);
                setProjectStats(response.data.statistics);
            }
            else {
                setFailure(`Error: ${response.status} - ${response.statusText}`);
            };
        })
        .catch((error) => {
            setFailure(error);
        });
};

const getQCCategoriesList = async (setCategories, setFailure) => {
    QualityControlService.getQCCategoriesList()
        .then((response) => {
            if(response.status > 199 && response.status < 300) {
                console.log(response.data);
                setCategories(response.data);
            }
        })
        .catch((error) => {
            setFailure(error);
        });
};

const QC_SEARCHABLE_FIELDS = [
    'area',
    'openingNumber',
    'cellNumber',
    'roomNumber',
    'roomName',
    'hardwareSet',
    'hardwareType',
    'elevation',
    'glassCode',
    'glassType',
    'itemDescription',
    'ceilingType',
    'partitionType'
];

const getInspectionFields = (categoryFields = {}) => {
    return Object.keys(categoryFields).filter(field => categoryFields[field]?.type === 'inspection');
};

const filterQCItems = (items = [], categoryFields = {}, { searchTerm, selectedArea, selectedLocation, activeFilter } = {}) => {
    let filteredItems = items.filter(item => item.scope?.toLowerCase() !== 'deleted');

    if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredItems = filteredItems.filter(item => {
            return QC_SEARCHABLE_FIELDS.some(field => {
                const val = item[field];
                return val && val.toString().toLowerCase().includes(searchLower);
            });
        });
    }

    if (selectedArea) {
        filteredItems = filteredItems.filter(item => item.area === selectedArea);
    }

    if (selectedLocation) {
        filteredItems = filteredItems.filter(item =>
            item.roomNumber === selectedLocation ||
            item.openingNumber === selectedLocation ||
            item.cellNumber === selectedLocation
        );
    }

    if (!activeFilter) {
        return filteredItems;
    }

    return filteredItems.filter(item => {
        const inspectionFields = getInspectionFields(categoryFields);

        if (activeFilter === 'needsInstalled') {
            const hasReceivedDate = item.receivedDate || item.receiveDate;
            const hasInstallDate = item.installerDate || item.installDate;
            return hasReceivedDate && !hasInstallDate;
        }

        if (activeFilter === 'needsRepaired') {
            const hasFailedInspection = inspectionFields.some(field => {
                if (item[field]) {
                    const value = item[field].toLowerCase();
                    return value === 'fail';
                }
                return false;
            });

            if (!hasFailedInspection) return false;

            const installDate = new Date(item.installerDate || item.installDate);
            const inspectionDate = item.inspectorDate || item.inspectionDate;

            if (inspectionDate) {
                const inspDate = new Date(inspectionDate);
                return inspDate > installDate;
            }
            return false;
        }

        if (activeFilter === 'needsReceived') {
            const hasReceivedDate = item.receivedDate || item.receiveDate;
            return !hasReceivedDate;
        }

        if (activeFilter === 'needsInspected') {
            const hasInstallDate = item.installerDate || item.installDate;
            if (!hasInstallDate) return false;

            const hasUninspectedField = inspectionFields.some(field => !item[field]);
            if (!hasUninspectedField) return false;

            const hasFailedInspection = inspectionFields.some(field => {
                if (item[field]) {
                    const value = item[field].toLowerCase();
                    return value === 'fail';
                }
                return false;
            });

            if (hasFailedInspection) {
                const installDate = new Date(item.installerDate || item.installDate);
                const inspectionDate = item.inspectorDate || item.inspectionDate;

                if (inspectionDate) {
                    const inspDate = new Date(inspectionDate);
                    return installDate > inspDate;
                }
                return false;
            }

            return true;
        }

        if (activeFilter === 'failedInspections') {
            return inspectionFields.some(field => {
                if (item[field]) {
                    const value = item[field].toLowerCase();
                    return value === 'fail';
                }
                return false;
            });
        }

        if (activeFilter === 'continueInspections') {
            if (inspectionFields.length === 0) return false;

            let hasAtLeastOneInput = false;
            let hasFail = false;
            let allCompleted = true;

            inspectionFields.forEach(field => {
                const value = item[field];
                if (value) {
                    hasAtLeastOneInput = true;
                    const lowerVal = value.toLowerCase();
                    if (lowerVal === 'fail') {
                        hasFail = true;
                    }
                } else {
                    allCompleted = false;
                }
            });

            return hasAtLeastOneInput && !hasFail && !allCompleted;
        }

        if (activeFilter === 'needsReinspected') {
            const hasFailedInspection = inspectionFields.some(field => {
                if (item[field]) {
                    const value = item[field].toLowerCase();
                    return value === 'fail';
                }
                return false;
            });

            if (!hasFailedInspection) return false;

            const installDate = new Date(item.installerDate || item.installDate);
            const inspectionDate = item.inspectorDate || item.inspectionDate;

            if (inspectionDate) {
                const inspDate = new Date(inspectionDate);
                return installDate > inspDate;
            }
            return false;
        }

        return true;
    });
};

const calculateSessionStats = (items = [], categoryFields = {}, sessionTypeLower) => {
    const total = items.length;

    if (sessionTypeLower === 'installer') {
        const onsite = items.filter(item => item.receivedDate || item.receiveDate).length;
        const installed = items.filter(item => item.installerDate || item.installDate).length;
        const leftToInstall = Math.max(total - installed, 0);
        const onsiteToInstall = Math.max(onsite - installed, 0);

        return {
            total,
            onsite,
            installed,
            leftToInstall,
            onsiteToInstall,
            percentOnsite: total > 0 ? (onsite / total) * 100 : 0,
            percentInstalled: total > 0 ? (installed / total) * 100 : 0
        };
    }

    if (sessionTypeLower === 'inspector') {
        const inspectionFields = getInspectionFields(categoryFields);
        const inspectableItems = items.filter(item => (item.installerDate || item.installDate) && inspectionFields.length > 0);
        const inspectable = inspectableItems.length;

        let inspected = 0;
        let activeInspections = 0;
        let failures = 0;

        inspectableItems.forEach(item => {
            let hasAtLeastOneInput = false;
            let hasFail = false;
            let allCompleted = true;

            inspectionFields.forEach(field => {
                const value = item[field];
                if (value) {
                    hasAtLeastOneInput = true;
                    const lowerVal = value.toLowerCase();
                    if (lowerVal === 'fail') {
                        hasFail = true;
                    }
                } else {
                    allCompleted = false;
                }
            });

            if (hasFail) {
                failures++;
            }

            if (allCompleted) {
                inspected++;
            } else if (hasAtLeastOneInput) {
                activeInspections++;
            }
        });

        const leftToInspect = Math.max(inspectable - inspected, 0);

        return {
            total,
            inspectable,
            activeInspections,
            inspected,
            leftToInspect,
            failures,
            percentInspected: inspectable > 0 ? (inspected / inspectable) * 100 : 0
        };
    }

    return { total };
};

// API CALLS ABOVE - - - - - - -

const QCSlideOutMenu = ({ isOpen, onClose, userName, userEmail, sessionType, projectData, qcCategories, activeFilter, setActiveFilter, selectedArea, setSelectedArea, selectedLocation, setSelectedLocation, onLogout }) => {
    
    const calculateFilterCounts = () => {
        if (!projectData || !qcCategories) return {};
        
        const counts = {
            needsInstalled: 0,
            needsRepaired: 0,
            needsReceived: 0,
            needsInspected: 0,
            failedInspections: 0,
            needsReinspected: 0,
            continueInspections: 0
        };
        
        // Iterate through all categories and items
        Object.keys(qcCategories).forEach(categoryKey => {
            const categoryFields = qcCategories[categoryKey]?.fields || {};
            const items = projectData[categoryKey] || [];
            
            items.forEach(item => {
                // Check for "Needs Received" - no received date
                const hasReceivedDate = item.receivedDate || item.receiveDate;
                if (!hasReceivedDate) {
                    counts.needsReceived++;
                }
                
                // Check for "Needs Installed" - has received date but no install date
                if (hasReceivedDate) {
                    const hasInstallDate = item.installerDate || item.installDate;
                    if (!hasInstallDate) {
                        counts.needsInstalled++;
                    }
                }
                
                // Check for "Needs Inspected" - has install date but at least one inspection field is null
                // Exclude items with failed inspections unless install date is more recent than inspection date
                const hasInstallDate = item.installerDate || item.installDate;
                if (hasInstallDate) {
                    const inspectionFields = Object.keys(categoryFields).filter(field => 
                        categoryFields[field]?.type === 'inspection'
                    );
                    const hasUninspectedField = inspectionFields.some(field => !item[field]);
                    
                    if (hasUninspectedField) {
                        // Check if there are any failed inspections
                        const hasFailedInspection = inspectionFields.some(field => {
                            if (item[field]) {
                                const value = item[field].toLowerCase();
                                return value === 'fail';
                            }
                            return false;
                        });
                        
                        if (hasFailedInspection) {
                            // Only count if install date is more recent than inspection date
                            const installDate = new Date(item.installerDate || item.installDate);
                            const inspectionDate = item.inspectorDate || item.inspectionDate;
                            
                            if (inspectionDate) {
                                const inspDate = new Date(inspectionDate);
                                if (installDate > inspDate) {
                                    counts.needsInspected++;
                                }
                            }
                        } else {
                            // No failed inspections, so count it
                            counts.needsInspected++;
                        }
                    }
                }
                
                // Check for "Failed Inspections" - has at least one fail in inspection fields
                const hasFailedInspection = Object.keys(categoryFields).some(field => {
                    const fieldType = categoryFields[field]?.type;
                    if (fieldType === 'inspection' && item[field]) {
                        const value = item[field].toLowerCase();
                        return value === 'fail';
                    }
                    return false;
                });
                if (hasFailedInspection) {
                    counts.failedInspections++;
                }
                
                // Check for "Needs Reinspected" - has failed inspection and install date is more recent than inspection date
                if (hasFailedInspection) {
                    const installDate = new Date(item.installerDate || item.installDate);
                    const inspectionDate = item.inspectorDate || item.inspectionDate;
                    
                    if (inspectionDate) {
                        const inspDate = new Date(inspectionDate);
                        if (installDate > inspDate) {
                            counts.needsReinspected++;
                        }
                    }
                }
                
                // Check for "Needs Repaired" - has fail in any inspection field AND inspection date is after install date
                const hasFailInInspection = Object.keys(categoryFields).some(field => {
                    const fieldType = categoryFields[field]?.type;
                    if (fieldType === 'inspection' && item[field]) {
                        const value = item[field].toLowerCase();
                        return value === 'fail';
                    }
                    return false;
                });
                
                if (hasFailInInspection) {
                    const installDate = new Date(item.installerDate || item.installDate);
                    const inspectionDate = item.inspectorDate || item.inspectionDate;
                    
                    if (inspectionDate) {
                        const inspDate = new Date(inspectionDate);
                        // Only count if inspection happened after installation
                        if (inspDate > installDate) {
                            counts.needsRepaired++;
                        }
                    }
                }
                
                // Check for "Continue Inspections" - has at least one inspection input, no fails, and not complete
                const inspectionFields = Object.keys(categoryFields).filter(field => 
                    categoryFields[field]?.type === 'inspection'
                );
                
                if (inspectionFields.length > 0) {
                    let hasAtLeastOneInput = false;
                    let hasFail = false;
                    let allCompleted = true;
                    
                    inspectionFields.forEach(field => {
                        const value = item[field];
                        if (value) {
                            hasAtLeastOneInput = true;
                            const lowerVal = value.toLowerCase();
                            if (lowerVal === 'fail') {
                                hasFail = true;
                            }
                        } else {
                            allCompleted = false;
                        }
                    });
                    
                    if (hasAtLeastOneInput && !hasFail && !allCompleted) {
                        counts.continueInspections++;
                    }
                }
            });
        });
        
        return counts;
    };
    
    const filterCounts = calculateFilterCounts();
    
    const getUniqueAreas = () => {
        if (!projectData || !qcCategories) return [];
        const areas = new Set();
        Object.keys(qcCategories).forEach(categoryKey => {
            const items = projectData[categoryKey] || [];
            items.forEach(item => {
                if (item.area) areas.add(item.area);
            });
        });
        return Array.from(areas).sort();
    };
    
    const getUniqueLocations = () => {
        if (!projectData || !qcCategories) return [];
        const locations = new Set();
        Object.keys(qcCategories).forEach(categoryKey => {
            const items = projectData[categoryKey] || [];
            items.forEach(item => {
                if (item.roomNumber) locations.add(item.roomNumber);
                if (item.openingNumber) locations.add(item.openingNumber);
                if (item.cellNumber) locations.add(item.cellNumber);
            });
        });
        return Array.from(locations).sort();
    };
    
    const uniqueAreas = getUniqueAreas();
    const uniqueLocations = getUniqueLocations();
    
    const handleFilterClick = (filterName) => {
        if (activeFilter === filterName) {
            setActiveFilter(null); // Deselect if already active
        } else {
            setActiveFilter(filterName);
        }
        onClose(); // Close menu after selecting filter
    };
    
    const handleAreaChange = (e) => {
        setSelectedArea(e.target.value);
    };
    
    const handleLocationChange = (e) => {
        setSelectedLocation(e.target.value);
    };
    
    const handleOpenHelp = () => {
        if (helpURL) {
            window.open(helpURL, '_blank', 'noopener,noreferrer');
        }
    };
    
    const renderInstallerFilters = () => (
        <>
            <div 
                className={`qc-filter-item ${activeFilter === 'needsInstalled' ? 'active' : ''}`}
                onClick={() => handleFilterClick('needsInstalled')}
            >
                <div className="qc-filter-name">Needs Installed</div>
                <div className="qc-filter-count">{filterCounts.needsInstalled}</div>
            </div>
            <div 
                className={`qc-filter-item ${activeFilter === 'needsRepaired' ? 'active' : ''}`}
                onClick={() => handleFilterClick('needsRepaired')}
            >
                <div className="qc-filter-name">Needs Repaired</div>
                <div className="qc-filter-count">{filterCounts.needsRepaired}</div>
            </div>
        </>
    );
    
    const renderReceivingFilters = () => (
        <>
            <div 
                className={`qc-filter-item ${activeFilter === 'needsReceived' ? 'active' : ''}`}
                onClick={() => handleFilterClick('needsReceived')}
            >
                <div className="qc-filter-name">Needs Received</div>
                <div className="qc-filter-count">{filterCounts.needsReceived}</div>
            </div>
        </>
    );
    
    const renderInspectorFilters = () => (
        <>
            <div 
                className={`qc-filter-item ${activeFilter === 'needsInspected' ? 'active' : ''}`}
                onClick={() => handleFilterClick('needsInspected')}
            >
                <div className="qc-filter-name">Needs Inspected</div>
                <div className="qc-filter-count">{filterCounts.needsInspected}</div>
            </div>
            <div 
                className={`qc-filter-item ${activeFilter === 'continueInspections' ? 'active' : ''}`}
                onClick={() => handleFilterClick('continueInspections')}
            >
                <div className="qc-filter-name">Continue Inspections</div>
                <div className="qc-filter-count">{filterCounts.continueInspections}</div>
            </div>
            <div 
                className={`qc-filter-item ${activeFilter === 'needsReinspected' ? 'active' : ''}`}
                onClick={() => handleFilterClick('needsReinspected')}
            >
                <div className="qc-filter-name">Needs Reinspected</div>
                <div className="qc-filter-count">{filterCounts.needsReinspected}</div>
            </div>
            <div 
                className={`qc-filter-item ${activeFilter === 'failedInspections' ? 'active' : ''}`}
                onClick={() => handleFilterClick('failedInspections')}
            >
                <div className="qc-filter-name">Failed Inspections</div>
                <div className="qc-filter-count">{filterCounts.failedInspections}</div>
            </div>
        </>
    );
    
    return (
        <>
            {isOpen && <div className="qc-menu-overlay" onClick={onClose} />}
            <div className={`qc-slide-out-menu ${isOpen ? 'open' : ''}`}>
                <div className="qc-menu-close-button" onClick={onClose}>
                    <IoClose />
                </div>
                <div className="qc-menu-user-info">
                    <div className="qc-menu-user-name">{userName}</div>
                    <div className="qc-menu-user-email">{userEmail}</div>
                </div>
                <div className="qc-menu-divider" />
                <div className="qc-menu-filters">
                    <div className="qc-menu-section-title">Filter by Location</div>
                    
                    <div className="qc-filter-select-group">
                        <label className="qc-filter-select-label">Area</label>
                        <select 
                            className="qc-filter-select" 
                            value={selectedArea} 
                            onChange={handleAreaChange}
                        >
                            <option value="">All Areas</option>
                            {uniqueAreas.map(area => (
                                <option key={area} value={area}>{area}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="qc-filter-select-group">
                        <label className="qc-filter-select-label">Room/Opening/Cell</label>
                        <select 
                            className="qc-filter-select" 
                            value={selectedLocation} 
                            onChange={handleLocationChange}
                        >
                            <option value="">All Locations</option>
                            {uniqueLocations.map(location => (
                                <option key={location} value={location}>{location}</option>
                            ))}
                        </select>
                    </div>
                    
                    {(selectedArea || selectedLocation) && (
                        <div className="qc-clear-filter" onClick={() => { setSelectedArea(''); setSelectedLocation(''); }}>
                            Clear Location Filters
                        </div>
                    )}
                    
                    <div className="qc-menu-divider" style={{ margin: '16px 0' }} />
                    <div className="qc-menu-section-title">Quick Filters</div>
                    {activeFilter && (
                        <div className="qc-clear-filter" onClick={() => { setActiveFilter(null); onClose(); }}>
                            Clear Active Filter
                        </div>
                    )}
                    {sessionType?.toLowerCase() === 'installer' && renderInstallerFilters()}
                    {sessionType?.toLowerCase() === 'receiving' && renderReceivingFilters()}
                    {sessionType?.toLowerCase() === 'inspector' && renderInspectorFilters()}
                </div>
                <div className="qc-menu-help">
                    <button className="qc-help-button" onClick={handleOpenHelp}>
                        Help
                    </button>
                </div>
                <div className="qc-menu-logout">
                    <button className="qc-logout-button" onClick={onLogout}>
                        Logout
                    </button>
                </div>
            </div>
        </>
    );
};

const QCProjectViewerHeader = ({ projectId, sessionType, searchTerm, setSearchTerm, hasQueuedUpdates, onSubmitUpdates, isSubmitting, userName, userEmail, projectData, qcCategories, activeFilter, setActiveFilter, selectedArea, setSelectedArea, selectedLocation, setSelectedLocation, onLogout }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    
    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };
    
    return (
        <>
            <div className="qc-project-viewer-header">
                <div className="qc-header-top">
                    <img src={logo} className="qc-header-logo" alt="Cornerstone Logo"/>
                    <div className='qc-header-project-info'>
                        <div className='qc-header-project-title'>{projectId}</div>
                        <div className='qc-header-project-subtitle'>{sessionType}</div>
                    </div>
                    {isMenuOpen ? (
                        <IoClose className='qc-header-menu-icon qc-header-menu-mobile' onClick={toggleMenu} />
                    ) : (
                        <GiHamburgerMenu className='qc-header-menu-icon qc-header-menu-mobile' onClick={toggleMenu} />
                    )}
                </div>
                <div className="qc-header-search-container">
                    <input 
                        type="text" 
                        className='qc-header-search-input'
                        placeholder="Search..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {hasQueuedUpdates && (
                    <button 
                        className="qc-submit-updates-button"
                        onClick={onSubmitUpdates}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Updates'}
                    </button>
                )}
                {isMenuOpen ? (
                    <IoClose className='qc-header-menu-icon qc-header-menu-desktop' onClick={toggleMenu} />
                ) : (
                    <GiHamburgerMenu className='qc-header-menu-icon qc-header-menu-desktop' onClick={toggleMenu} />
                )}
            </div>
            <QCSlideOutMenu 
                isOpen={isMenuOpen} 
                onClose={() => setIsMenuOpen(false)} 
                userName={userName} 
                userEmail={userEmail}
                sessionType={sessionType}
                projectData={projectData}
                qcCategories={qcCategories}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                selectedArea={selectedArea}
                setSelectedArea={setSelectedArea}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                onLogout={onLogout}
            />
        </>
    );
}

const HardwareGroupHeader = ({ groupKey, items, isExpanded, toggleExpanded, categoryKey, qcCategories, sessionType, onQueueUpdate, onClearUpdate, queuedUpdates, userName }) => {
    // Determine the lowest status level across all items in the group
    const getGroupStatus = () => {
        if (!items || items.length === 0) return 'not-onsite';
        
        let lowestStatus = 'installed'; // Start with highest status
        
        items.forEach(item => {
            const queueKey = `${categoryKey}_${item.id}`;
            const displayItem = queuedUpdates.hasOwnProperty(queueKey) ? queuedUpdates[queueKey] : item;
            
            const hasReceivedDate = displayItem.receivedDate || displayItem.receiveDate;
            const hasInstallDate = displayItem.installerDate || displayItem.installDate;
            
            if (!hasReceivedDate) {
                lowestStatus = 'not-onsite'; // Lowest level - stop checking
            } else if (!hasInstallDate && lowestStatus !== 'not-onsite') {
                lowestStatus = 'onsite'; // Middle level
            }
            // If hasInstallDate, status is 'installed' - keep current unless lower found
        });
        
        return lowestStatus;
    };
    
    const groupStatus = getGroupStatus();
    const headerClass = groupStatus === 'installed' ? 'qc-header-pass' : 
                       groupStatus === 'onsite' ? 'qc-header-installed' : '';
    
    return (
        <>
            <div className={`qc-hardware-group-header ${headerClass}`} onClick={toggleExpanded}>
                <span className={`qc-hardware-group-toggle ${isExpanded ? 'expanded' : ''}`}>▼</span>
                <div className="qc-hardware-group-title">{groupKey}</div>
                <span className="qc-hardware-group-count">{items.length} items</span>
            </div>
            {isExpanded && (
                <div className="qc-hardware-group-items">
                    {items.map((item, index) => {
                        const queueKey = `${categoryKey}_${item.id}`;
                        const hasQueuedChanges = queuedUpdates.hasOwnProperty(queueKey);
                        const displayItem = hasQueuedChanges ? queuedUpdates[queueKey] : item;
                        
                        return (
                            <QCItemCard 
                                key={item.id || index}
                                item={displayItem}
                                categoryKey={categoryKey}
                                qcCategories={qcCategories}
                                sessionType={sessionType}
                                onQueueUpdate={onQueueUpdate}
                                onClearUpdate={onClearUpdate}
                                hasQueuedChanges={hasQueuedChanges}
                                userName={userName}
                                isHardwareItem={true}
                            />
                        );
                    })}
                </div>
            )}
        </>
    );
};

const QCCategoryItem = ({ category, categoryKey, items, qcCategories, sessionType, searchTerm, onQueueUpdate, onClearUpdate, queuedUpdates, userName, activeFilter, selectedArea, selectedLocation }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    
    if (!items || items.length === 0) return null;
    
    const categoryFields = qcCategories?.[categoryKey]?.fields || {};
    
    const filteredItems = filterQCItems(items, categoryFields, {
        searchTerm,
        selectedArea,
        selectedLocation,
        activeFilter
    });

    if (filteredItems.length === 0) return null;

    // Check if this is the Hardware category
    const isHardwareCategory = category.toLowerCase() === 'hardware';
    
    // Group hardware items by area and opening number
    const groupedHardwareItems = {};
    if (isHardwareCategory) {
        filteredItems.forEach(item => {
            const area = item.area || 'Unknown Area';
            const openingNumber = item.openingNumber || 'Unknown Opening';
            const groupKey = `${area} - ${openingNumber}`;
            
            if (!groupedHardwareItems[groupKey]) {
                groupedHardwareItems[groupKey] = [];
            }
            groupedHardwareItems[groupKey].push(item);
        });
    }
    
    const toggleGroupExpanded = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    return (
        <div className="qc-category-section">
            <div className="qc-category-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h2 className="qc-category-title">{category}</h2>
                <span className="qc-category-count">{filteredItems.length} items</span>
                <span className={`qc-category-toggle ${isExpanded ? 'expanded' : ''}`}>▼</span>
            </div>
            {isExpanded && (
                <div className="qc-category-items">
                    {isHardwareCategory ? (
                        // Render grouped hardware items
                        Object.keys(groupedHardwareItems).sort().map(groupKey => (
                            <HardwareGroupHeader
                                key={groupKey}
                                groupKey={groupKey}
                                items={groupedHardwareItems[groupKey]}
                                isExpanded={expandedGroups[groupKey] === true}
                                toggleExpanded={() => toggleGroupExpanded(groupKey)}
                                categoryKey={categoryKey}
                                qcCategories={qcCategories}
                                sessionType={sessionType}
                                onQueueUpdate={onQueueUpdate}
                                onClearUpdate={onClearUpdate}
                                queuedUpdates={queuedUpdates}
                                userName={userName}
                            />
                        ))
                    ) : (
                        // Render regular items
                        filteredItems.map((item, index) => {
                            const queueKey = `${categoryKey}_${item.id}`;
                            const hasQueuedChanges = queuedUpdates.hasOwnProperty(queueKey);
                            const displayItem = hasQueuedChanges ? queuedUpdates[queueKey] : item;
                            
                            return (
                                <QCItemCard 
                                    key={item.id || index}
                                    item={displayItem}
                                    categoryKey={categoryKey}
                                    qcCategories={qcCategories}
                                    sessionType={sessionType}
                                    onQueueUpdate={onQueueUpdate}
                                    onClearUpdate={onClearUpdate}
                                    hasQueuedChanges={hasQueuedChanges}
                                    userName={userName}
                                />
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const QCItemCard = ({ item, categoryKey, qcCategories, sessionType, onQueueUpdate, onClearUpdate, hasQueuedChanges, userName, isHardwareItem = false }) => {
    const [editedItem, setEditedItem] = useState(item);
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const categoryFields = qcCategories?.[categoryKey]?.fields || {};
    const sessionTypeLower = sessionType.toLowerCase();
    const sessionTypeKey = sessionTypeLower === 'receiving' ? 'receiving' : 
                          sessionTypeLower === 'installer' ? 'installer' : 'inspector';

    const getFieldPermission = (field) => {
        const permission = categoryFields[field]?.[sessionTypeKey] || 'hidden';
        return permission;
    };

    const isPrepopulateField = (field) => {
        return categoryFields[field]?.[sessionTypeKey] === 'prepopulate';
    };

    const getFieldType = (field) => {
        return categoryFields[field]?.type || 'text';
    };

    const getFieldDisplayName = (field) => {
        return categoryFields[field]?.displayName || field;
    };

    const handleFieldChange = (field, value) => {
        // For installQuantity, allow the input to change but validate later on blur
        let newItem = { ...editedItem, [field]: value };
        
        // Add/update prepopulated fields when editing
        Object.keys(categoryFields).forEach(fieldKey => {
            if (isPrepopulateField(fieldKey)) {
                newItem[fieldKey] = userName;
            }
        });
        
        setEditedItem(newItem);
        
        // Queue the update immediately when field changes
        if (onQueueUpdate) {
            onQueueUpdate(categoryKey, item.id, newItem);
        }
    };

    const handleUndo = () => {
        // Clear the queued update for this item
        if (onClearUpdate) {
            onClearUpdate(categoryKey, item.id);
        }
        // Reset the edited item to the original
        setEditedItem(item);
    };

    const visibleFields = Object.keys(item).filter(field => {
        const permission = getFieldPermission(field);
        if (permission === 'hidden' || field === 'id' || field === 'project') {
            return false;
        }
        
        // Hide area, location fields, and scope since they're in the header or not needed
        if (field === 'area' || field === 'openingNumber' || field === 'cellNumber' || field === 'roomNumber' || field === 'scope') {
            return false;
        }
        
        // For hardware items, hide hardwareSet and hardwareType fields
        if (isHardwareItem && (field === 'hardwareSet' || field === 'hardwareType' || field === 'set' || field === 'type')) {
            return false;
        }
        
        return true;
    });

    // For installer session type, add failed inspection fields even if normally hidden
    // BUT only if inspection date is after install date
    if (sessionTypeLower === 'installer') {
        const installDate = new Date(item.installerDate || item.installDate);
        const inspectionDate = item.inspectorDate || item.inspectionDate;
        let showFailedInspections = false;
        
        if (inspectionDate) {
            const inspDate = new Date(inspectionDate);
            // Only show failed inspections if inspection happened after installation
            showFailedInspections = inspDate > installDate;
        }
        
        if (showFailedInspections) {
            Object.keys(categoryFields).forEach(field => {
                const fieldType = getFieldType(field);
                if (fieldType === 'inspection' && item[field]) {
                    const value = item[field].toLowerCase();
                    if (value === 'fail' && !visibleFields.includes(field)) {
                        visibleFields.push(field);
                    }
                }
            });
            
            // Also show comments field if any inspection has failed
            const hasFailedInspection = Object.keys(categoryFields).some(field => {
                const fieldType = getFieldType(field);
                return fieldType === 'inspection' && item[field] && item[field].toLowerCase() === 'fail';
            });
            
            if (hasFailedInspection && item.comments && !visibleFields.includes('comments')) {
                visibleFields.push('comments');
            }
        }
    }

    const editableFields = visibleFields.filter(field => getFieldPermission(field) === 'edit');
    
    // For inspector session type, disable editing if no install date
    const inspectorCanEdit = sessionTypeLower === 'inspector' ? (item.installerDate || item.installDate) : true;
    const canEdit = editableFields.length > 0 && inspectorCanEdit;

    // Calculate inspection status for header color
    const getInspectionStatus = () => {
        const inspectionFields = visibleFields.filter(field => getFieldType(field) === 'inspection');
        
        if (inspectionFields.length === 0) return 'none';
        
        // For installer session type, only consider inspections if inspection date is after install date
        if (sessionTypeLower === 'installer') {
            const installDate = new Date(item.installerDate || item.installDate);
            const inspectionDate = item.inspectorDate || item.inspectionDate;
            
            if (inspectionDate) {
                const inspDate = new Date(inspectionDate);
                // If inspection was before reinstall, don't show as failed
                if (inspDate <= installDate) {
                    return 'none';
                }
            }
        }
        
        let hasFail = false;
        let allPassedOrNA = true;
        let hasAnyInspectionData = false;
        
        inspectionFields.forEach(field => {
            const value = item[field];
            if (value) {
                hasAnyInspectionData = true;
                const lowerVal = value.toLowerCase();
                if (lowerVal === 'fail') {
                    hasFail = true;
                }
                if (lowerVal !== 'pass' && lowerVal !== 'n/a' && lowerVal !== 'not applicable') {
                    allPassedOrNA = false;
                }
            } else {
                allPassedOrNA = false;
            }
        });
        
        if (hasFail) return 'fail';
        if (allPassedOrNA) return 'pass';
        return hasAnyInspectionData ? 'partial' : 'none';
    };

    const inspectionStatus = getInspectionStatus();
    const hasInstallDate = item.installerDate || item.installDate;
    const hasInspectionInProgress = inspectionStatus === 'partial' && hasInstallDate;
    const showInstallColors = sessionTypeLower === 'installer' || sessionTypeLower === 'inspector';
    const hasReceivedDate = sessionTypeLower === 'receiving' && (item.receivedDate || item.receivingdDate);
    
    // For installer session type
    const installerHasReceived = sessionTypeLower === 'installer' && (item.receivedDate || item.receivingdDate);
    const installerHasInstalled = sessionTypeLower === 'installer' && hasInstallDate;
    
    // Determine card identifier based on whether it's a hardware item
    const getCardIdentifier = () => {
        if (isHardwareItem) {
            // For hardware items, show hardware set and hardware type
            const hardwareSet = item.hardwareSet || item.set || 'Unknown Set';
            const hardwareType = item.hardwareType || item.type || 'Unknown Type';
            return `${hardwareSet} - ${hardwareType}`;
        } else {
            // For non-hardware items, show area and location
            return `${item.area} - ${item.roomNumber || item.openingNumber || item.cellNumber || `Item ${item.id}`}`;
        }
    };

    return (
        <div className="qc-item-card">
            <div className={`qc-item-card-header ${inspectionStatus === 'fail' ? 'qc-header-fail' : inspectionStatus === 'pass' ? 'qc-header-pass' : hasReceivedDate ? 'qc-header-pass' : installerHasInstalled ? 'qc-header-pass' : installerHasReceived ? 'qc-header-installed' : showInstallColors && hasInspectionInProgress ? 'qc-header-inspection-progress' : showInstallColors && hasInstallDate ? 'qc-header-installed' : ''}`} onClick={() => {
                if (!isExpanded && canEdit) {
                    setIsEditing(true);
                }
                setIsExpanded(!isExpanded);
            }}>
                <span className={`qc-item-toggle ${isExpanded ? 'expanded' : ''}`}>▼</span>
                <div className="qc-item-identifier">
                    {getCardIdentifier()}
                </div>
                {isEditing && hasQueuedChanges && (
                    <button 
                        className="qc-done-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUndo();
                        }}
                    >
                        Undo
                    </button>
                )}
            </div>
            {hasQueuedChanges && (
                <div className="qc-updates-queued">
                    Updates Queued for Submission
                </div>
            )}
            {isExpanded && (
                <>
                    <div className="qc-item-fields">
                        {visibleFields.map(field => {
                            const permission = getFieldPermission(field);
                            const fieldType = getFieldType(field);
                            const displayName = getFieldDisplayName(field);
                            const isPrepopulate = isPrepopulateField(field);
                            const value = isEditing ? editedItem[field] : item[field];
                            const isEditable = permission === 'edit' && isEditing && inspectorCanEdit;
                            // Only treat as prepopulated if there are queued changes
                            const showAsPrepopulated = isPrepopulate && hasQueuedChanges;

                            return (
                                <QCFieldDisplay
                                    key={field}
                                    field={field}
                                    displayName={displayName}
                                    value={value}
                                    fieldType={fieldType}
                                    isEditable={isEditable}
                                    isPrepopulate={showAsPrepopulated}
                                    userName={userName}
                                    item={item}
                                    onChange={(newValue) => handleFieldChange(field, newValue)}
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

const QCFieldDisplay = ({ field, displayName, value, fieldType, isEditable, isPrepopulate, userName, item, onChange }) => {
    // Check if this is a date field with the placeholder value '0001-01-01'
    const isPlaceholderDate = fieldType === 'date' && value && value.toString().startsWith('0001-01-01');
    
    // Don't render date fields with placeholder value
    if (isPlaceholderDate) {
        return null;
    }
    
    const formatValue = (val, type) => {
        if (val === null || val === undefined || val === '') return '';
        if (type === 'date') {
            const date = new Date(val);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        }
        return val.toString();
    };

    const displayValue = (val, type) => {
        if (val === null || val === undefined) return '-';
        if (type === 'date') {
            const date = new Date(val);
            return date.toLocaleDateString();
        }
        if (type === 'inspection') {
            return val || 'Not inspected';
        }
        return val.toString();
    };

    const getInspectionClass = (val) => {
        if (!val) return '';
        const lowerVal = val.toLowerCase();
        if (lowerVal === 'pass') return 'qc-inspection-pass';
        if (lowerVal === 'fail') return 'qc-inspection-fail';
        if (lowerVal === 'n/a' || lowerVal === 'not applicable') return 'qc-inspection-na';
        return '';
    };

    const handleInstallQuantityBlur = (e) => {
        if (field !== 'installQuantity') return;
        
        const quantityField = item.hardwareQuantity ? 'hardwareQuantity' : 
                             item.glassQuantity ? 'glassQuantity' : 
                             item.itemQuantity ? 'itemQuantity' : 
                             item.panelCount ? 'panelCount' : null;
        
        let inputValue = e.target.value;
        let installQty = parseInt(inputValue);
        
        // If empty or invalid, set to minimum value of 1
        if (!inputValue || isNaN(installQty) || installQty < 1) {
            onChange('1');
            return;
        }
        
        // Check against maximum quantity if available
        if (quantityField && item[quantityField]) {
            const maxQuantity = parseInt(item[quantityField]);
            if (!isNaN(maxQuantity) && installQty > maxQuantity) {
                onChange(maxQuantity.toString());
                return;
            }
        }
        
        // Ensure it's a whole number
        onChange(installQty.toString());
    };

    const handleReceivedQuantityBlur = (e) => {
        if (field !== 'receivedQuantity') return;
        
        const quantityField = item.hardwareQuantity ? 'hardwareQuantity' : 
                             item.glassQuantity ? 'glassQuantity' : 
                             item.itemQuantity ? 'itemQuantity' : 
                             item.panelCount ? 'panelCount' : null;
        
        let inputValue = e.target.value;
        let receivedQty = parseInt(inputValue);
        
        // If empty or invalid, set to minimum value of 1
        if (!inputValue || isNaN(receivedQty) || receivedQty < 1) {
            onChange('1');
            return;
        }
        
        // Check against maximum quantity if available
        if (quantityField && item[quantityField]) {
            const maxQuantity = parseInt(item[quantityField]);
            if (!isNaN(maxQuantity) && receivedQty > maxQuantity) {
                onChange(maxQuantity.toString());
                return;
            }
        }
        
        // Ensure it's a whole number
        onChange(receivedQty.toString());
    };

    const handleInstallDateBlur = (e) => {
        if (field !== 'installerDate' && field !== 'installDate') return;
        
        const receivedDate = item.receivedDate || item.receiveDate;
        if (!receivedDate) return; // No received date to validate against
        
        const installDateValue = e.target.value;
        if (!installDateValue) return; // Empty date is allowed
        
        const receivedDateObj = new Date(receivedDate);
        const installDateObj = new Date(installDateValue);
        
        // If install date is before received date, set it to received date
        if (installDateObj < receivedDateObj) {
            onChange(receivedDate);
        }
    };

    const handleReceivedDateBlur = (e) => {
        if (field !== 'receivedDate' && field !== 'receiveDate') return;
        
        const receivedDateValue = e.target.value;
        if (!receivedDateValue) return; // Empty date is allowed
        
        // Get all possible ship/release dates
        const dates = [];
        if (item.scheduledShipDate) dates.push(new Date(item.scheduledShipDate));
        if (item.releaseDate) dates.push(new Date(item.releaseDate));
        if (item.actualShipDate) dates.push(new Date(item.actualShipDate));
        
        if (dates.length === 0) return; // No dates to validate against
        
        // Find the largest (most recent) date
        const maxDate = new Date(Math.max(...dates));
        const receivedDateObj = new Date(receivedDateValue);
        
        // If received date is before the max ship/release date, set it to that date
        if (receivedDateObj < maxDate) {
            onChange(maxDate.toISOString().split('T')[0]);
        }
    };

    // Handle prepopulate fields
    const effectiveValue = isPrepopulate ? (value || userName) : value;

    return (
        <div className="qc-field">
            <label className="qc-field-label">{displayName}</label>
            {isPrepopulate ? (
                <div className="qc-field-value qc-field-prepopulated">{effectiveValue || '-'}</div>
            ) : isEditable ? (
                fieldType === 'date' ? (
                    <input
                        type="date"
                        className="qc-field-input"
                        value={formatValue(value, fieldType)}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={(field === 'installerDate' || field === 'installDate') ? handleInstallDateBlur : 
                               (field === 'receivedDate' || field === 'receiveDate') ? handleReceivedDateBlur : undefined}
                        min={(field === 'installerDate' || field === 'installDate') && (item?.receivedDate || item?.receiveDate) ? 
                            new Date(item.receivedDate || item.receiveDate).toISOString().split('T')[0] : 
                            (field === 'receivedDate' || field === 'receiveDate') ? (() => {
                                const dates = [];
                                if (item?.scheduledShipDate) dates.push(new Date(item.scheduledShipDate));
                                if (item?.releaseDate) dates.push(new Date(item.releaseDate));
                                if (item?.actualShipDate) dates.push(new Date(item.actualShipDate));
                                if (dates.length > 0) {
                                    const maxDate = new Date(Math.max(...dates));
                                    return maxDate.toISOString().split('T')[0];
                                }
                                return undefined;
                            })() : undefined}
                    />
                ) : fieldType === 'number' ? (
                    <input
                        type="number"
                        className="qc-field-input"
                        value={formatValue(value, fieldType)}
                        min={field === 'installQuantity' || field === 'receivedQuantity' ? '1' : '0'}
                        step={field === 'installQuantity' || field === 'receivedQuantity' ? '1' : 'any'}
                        max={field === 'installQuantity' || field === 'receivedQuantity' ? (
                            item?.hardwareQuantity || item?.glassQuantity || item?.itemQuantity || item?.panelCount || undefined
                        ) : undefined}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={field === 'installQuantity' ? handleInstallQuantityBlur : 
                               field === 'receivedQuantity' ? handleReceivedQuantityBlur : undefined}
                    />
                ) : fieldType === 'inspection' ? (
                    <select
                        className="qc-field-input"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                    >
                        <option value="">Not inspected</option>
                        <option value="pass">Pass</option>
                        <option value="fail">Fail</option>
                        <option value="N/A">Not Applicable</option>
                    </select>
                ) : fieldType === 'textArea' ? (
                    <textarea
                        className="qc-field-textarea"
                        value={formatValue(value, fieldType)}
                        onChange={(e) => onChange(e.target.value)}
                        rows={4}
                    />
                ) : (
                    <input
                        type="text"
                        className="qc-field-input"
                        value={formatValue(value, fieldType)}
                        onChange={(e) => onChange(e.target.value)}
                    />
                )
            ) : (
                <div className={`qc-field-value ${fieldType === 'inspection' ? getInspectionClass(value) : ''}`}>
                    {displayValue(value, fieldType)}
                </div>
            )}
        </div>
    );
};

const QCStatisticsSection = ({ projectData, qcCategories, sessionType, searchTerm, activeFilter, selectedArea, selectedLocation }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!projectData || !qcCategories) return null;
    
    const sessionTypeLower = sessionType?.toLowerCase();
    if (sessionTypeLower !== 'installer' && sessionTypeLower !== 'inspector') return null;
    
    const allCategoryKeys = Object.keys(qcCategories);

    const categoryStats = allCategoryKeys
        .map(categoryKey => {
            const categoryConfig = qcCategories[categoryKey];
            const categoryFields = categoryConfig?.fields || {};
            const items = projectData[categoryKey] || [];
            const filteredItems = filterQCItems(items, categoryFields, {
                searchTerm,
                selectedArea,
                selectedLocation,
                activeFilter
            });

            if (filteredItems.length === 0) return null;

            return {
                categoryKey,
                displayName: categoryConfig?.displayName || categoryKey,
                stats: calculateSessionStats(filteredItems, categoryFields, sessionTypeLower)
            };
        })
        .filter(Boolean);

    const totalStats = categoryStats.reduce((acc, category) => {
        const stats = category.stats;
        acc.total += stats.total || 0;

        if (sessionTypeLower === 'installer') {
            acc.onsite += stats.onsite || 0;
            acc.installed += stats.installed || 0;
            acc.leftToInstall += stats.leftToInstall || 0;
            acc.onsiteToInstall += stats.onsiteToInstall || 0;
        }

        if (sessionTypeLower === 'inspector') {
            acc.inspectable += stats.inspectable || 0;
            acc.activeInspections += stats.activeInspections || 0;
            acc.inspected += stats.inspected || 0;
            acc.leftToInspect += stats.leftToInspect || 0;
            acc.failures += stats.failures || 0;
        }

        return acc;
    }, {
        total: 0,
        onsite: 0,
        installed: 0,
        leftToInstall: 0,
        onsiteToInstall: 0,
        inspectable: 0,
        activeInspections: 0,
        inspected: 0,
        leftToInspect: 0,
        failures: 0
    });

    totalStats.percentOnsite = totalStats.total > 0 ? (totalStats.onsite / totalStats.total) * 100 : 0;
    totalStats.percentInstalled = totalStats.total > 0 ? (totalStats.installed / totalStats.total) * 100 : 0;
    totalStats.percentInspected = totalStats.inspectable > 0 ? (totalStats.inspected / totalStats.inspectable) * 100 : 0;

    if (categoryStats.length === 0) return null;
    
    return (
        <div className="qc-category-section">
            <div className="qc-category-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h2 className="qc-category-title">Statistics</h2>
                <span className={`qc-category-toggle ${isExpanded ? 'expanded' : ''}`}>▼</span>
            </div>
            {isExpanded && (
                <div className="qc-statistics-content">
                    {/* Total Stats */}
                    {totalStats && (
                        <div className="qc-stats-card qc-stats-total">
                            <h3 className="qc-stats-card-title">Filtered Totals</h3>
                            <div className="qc-stats-grid">
                                <div className="qc-stat-item">
                                    <div className="qc-stat-label">Total Items</div>
                                    <div className="qc-stat-value">{totalStats.total}</div>
                                </div>
                                {sessionTypeLower === 'installer' && (
                                    <div className="qc-stat-item">
                                        <div className="qc-stat-label">Onsite</div>
                                        <div className="qc-stat-value">{totalStats.onsite} ({totalStats.percentOnsite.toFixed(1)}%)</div>
                                    </div>
                                )}
                                {sessionTypeLower === 'inspector' && (
                                    <div className="qc-stat-item">
                                        <div className="qc-stat-label">Active Inspections</div>
                                        <div className="qc-stat-value">{totalStats.activeInspections}</div>
                                    </div>
                                )}
                                {sessionTypeLower === 'installer' && (
                                    <>
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Installed</div>
                                            <div className="qc-stat-value">{totalStats.installed} ({totalStats.percentInstalled.toFixed(1)}%)</div>
                                        </div>
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Left to Install</div>
                                            <div className="qc-stat-value">{totalStats.leftToInstall}</div>
                                        </div>
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Onsite to Install</div>
                                            <div className="qc-stat-value">{totalStats.onsiteToInstall}</div>
                                        </div>
                                    </>
                                )}
                                {sessionTypeLower === 'inspector' && (
                                    <>
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Inspected</div>
                                            <div className="qc-stat-value">{totalStats.inspected} ({totalStats.percentInspected.toFixed(1)}%)</div>
                                        </div>
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Left to Inspect</div>
                                            <div className="qc-stat-value">{totalStats.leftToInspect}</div>
                                        </div>
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Failures</div>
                                            <div className="qc-stat-value">{totalStats.failures}</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Category Stats */}
                    <div className="qc-stats-categories">
                        {categoryStats.map(category => {
                            const stats = category.stats;
                            if (!stats) return null;
                            
                            return (
                                <div key={category.categoryKey} className="qc-stats-card">
                                    <h4 className="qc-stats-card-title">{category.displayName}</h4>
                                    <div className="qc-stats-grid">
                                        <div className="qc-stat-item">
                                            <div className="qc-stat-label">Total</div>
                                            <div className="qc-stat-value">{stats.total}</div>
                                        </div>
                                        {sessionTypeLower === 'installer' && (
                                            <div className="qc-stat-item">
                                                <div className="qc-stat-label">Onsite</div>
                                                <div className="qc-stat-value">{stats.onsite} ({stats.percentOnsite.toFixed(1)}%)</div>
                                            </div>
                                        )}
                                        {sessionTypeLower === 'inspector' && (
                                            <div className="qc-stat-item">
                                                <div className="qc-stat-label">Active Inspections</div>
                                                <div className="qc-stat-value">{stats.activeInspections}</div>
                                            </div>
                                        )}
                                        {sessionTypeLower === 'installer' && (
                                            <>
                                                <div className="qc-stat-item">
                                                    <div className="qc-stat-label">Installed</div>
                                                    <div className="qc-stat-value">{stats.installed} ({stats.percentInstalled.toFixed(1)}%)</div>
                                                </div>
                                                <div className="qc-stat-item">
                                                    <div className="qc-stat-label">Left to Install</div>
                                                    <div className="qc-stat-value">{stats.leftToInstall}</div>
                                                </div>
                                            </>
                                        )}
                                        {sessionTypeLower === 'inspector' && (
                                            <>
                                                <div className="qc-stat-item">
                                                    <div className="qc-stat-label">Inspected</div>
                                                    <div className="qc-stat-value">{stats.inspected} ({stats.percentInspected.toFixed(1)}%)</div>
                                                </div>
                                                <div className="qc-stat-item">
                                                    <div className="qc-stat-label">Left to Inspect</div>
                                                    <div className="qc-stat-value">{stats.leftToInspect}</div>
                                                </div>
                                                <div className="qc-stat-item">
                                                    <div className="qc-stat-label">Failures</div>
                                                    <div className="qc-stat-value">{stats.failures}</div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const QCProjectViewerContent = ({ projectData, projectStats, qcCategories, searchTerm, sessionType, onQueueUpdate, onClearUpdate, queuedUpdates, userName, activeFilter, selectedArea, selectedLocation }) => {
    if (!projectData) {
        return (
            <div className="qc-project-viewer-content">
                <div className="qc-loading">
                    Loading project data...
                </div>
            </div>
        );
    }

    const categories = qcCategories ? Object.keys(qcCategories) : [];

    // Check if any category has visible items after filtering
    let hasAnyVisibleItems = false;
    
    const renderedCategories = categories.map(categoryKey => {
        const category = qcCategories[categoryKey];
        const items = projectData[categoryKey] || [];
        
        // Apply the same filtering logic as QCCategoryItem
        if (items.length > 0) {
            const categoryFields = qcCategories?.[categoryKey]?.fields || {};
            const filteredItems = filterQCItems(items, categoryFields, {
                searchTerm,
                selectedArea,
                selectedLocation,
                activeFilter
            });

            if (filteredItems.length > 0) {
                hasAnyVisibleItems = true;
            }
        }
        
        return (
            <QCCategoryItem
                key={categoryKey}
                category={category.displayName}
                categoryKey={categoryKey}
                items={items}
                qcCategories={qcCategories}
                sessionType={sessionType}
                searchTerm={searchTerm}
                onQueueUpdate={onQueueUpdate}
                onClearUpdate={onClearUpdate}
                queuedUpdates={queuedUpdates}
                userName={userName}
                activeFilter={activeFilter}
                selectedArea={selectedArea}
                selectedLocation={selectedLocation}
            />
        );
    });

    return (
        <div className="qc-project-viewer-content">
            <QCStatisticsSection 
                projectData={projectData}
                qcCategories={qcCategories}
                sessionType={sessionType}
                searchTerm={searchTerm}
                activeFilter={activeFilter}
                selectedArea={selectedArea}
                selectedLocation={selectedLocation}
            />
            {renderedCategories}
            {categories.length === 0 && (
                <div className="qc-no-categories">
                    No categories available.
                </div>
            )}
            {categories.length > 0 && !hasAnyVisibleItems && (
                <div className="qc-no-records">
                    No records to display. Please check filter settings.
                </div>
            )}
        </div>
    );
};

const QCProjectViewer = ({ projectId, sessionType, searchTerm, setSearchTerm, projectData, projectStats, qcCategories, sessionId, userName, userEmail, refreshProjectData, onLogout }) => {
    const [queuedUpdates, setQueuedUpdates] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');

    // Add beforeunload event listener to warn user about unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (Object.keys(queuedUpdates).length > 0) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [queuedUpdates]);

    const handleQueueUpdate = (categoryKey, itemId, updatedItem) => {
        const queueKey = `${categoryKey}_${itemId}`;
        setQueuedUpdates(prev => ({
            ...prev,
            [queueKey]: updatedItem
        }));
        setSubmitError(null);
        setSubmitSuccess(false);
    };

    const handleClearUpdate = (categoryKey, itemId) => {
        const queueKey = `${categoryKey}_${itemId}`;
        setQueuedUpdates(prev => {
            const newUpdates = { ...prev };
            delete newUpdates[queueKey];
            return newUpdates;
        });
    };

    const handleSubmitUpdates = async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            // Build update object array with proper structure
            const updateObject = [];
            
            Object.keys(queuedUpdates).forEach(queueKey => {
                const [categoryKey, itemId] = queueKey.split('_');
                const updatedItem = queuedUpdates[queueKey];
                
                // Find the original item in projectData
                const categoryItems = projectData[categoryKey] || [];
                const itemIndex = categoryItems.findIndex(item => item.id === parseInt(itemId));
                const originalItem = categoryItems[itemIndex];
                
                if (originalItem) {
                    // Calculate only the changed fields
                    const updates = {};
                    Object.keys(updatedItem).forEach(key => {
                        if (updatedItem[key] !== originalItem[key]) {
                            updates[key] = updatedItem[key];
                        }
                    });
                    
                    // For inspector session type, check if inspection fields were updated
                    if (sessionType?.toLowerCase() === 'inspector' && Object.keys(updates).length > 0) {
                        const categoryFields = qcCategories?.[categoryKey]?.fields || {};
                        
                        // Check if any inspection field was updated
                        const hasInspectionUpdate = Object.keys(updates).some(key => 
                            categoryFields[key]?.type === 'inspection'
                        );
                        
                        // If inspection fields were updated but not the inspection date, set it to today
                        if (hasInspectionUpdate && !updates.inspectorDate && !updates.inspectionDate) {
                            const today = new Date().toISOString().split('T')[0];
                            updates.inspectorDate = today;
                        }
                    }
                    
                    // Only add to update object if there are actual changes
                    if (Object.keys(updates).length > 0) {
                        updateObject.push({
                            sectionName: categoryKey,
                            itemIndex: itemIndex,
                            updates: updates,
                            originalItem: originalItem
                        });
                    }
                }
            });

            console.log('Update Object:', updateObject);

            // Make a single API call with all updates
            if (updateObject.length > 0) {
                await QualityControlService.batchUpdateQCItems(
                    sessionId,
                    projectId,
                    updateObject,
                    userName
                ).then(() => {
                    refreshProjectData();
                });
            }
            
            setSubmitSuccess(true);
            setQueuedUpdates({});
            setTimeout(() => setSubmitSuccess(false), 3000);
        } catch (error) {
            console.error('Error submitting updates:', error);
            setSubmitError(error.response?.data?.message || 'Failed to submit updates... Please check internet connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasQueuedUpdates = Object.keys(queuedUpdates).length > 0;

    return (
        <div className="qc-project-viewer">
            <QCProjectViewerHeader 
                projectId={projectId} 
                sessionType={sessionType} 
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm}
                hasQueuedUpdates={hasQueuedUpdates}
                onSubmitUpdates={handleSubmitUpdates}
                isSubmitting={isSubmitting}
                userName={userName}
                userEmail={userEmail}
                projectData={projectData}
                qcCategories={qcCategories}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                selectedArea={selectedArea}
                setSelectedArea={setSelectedArea}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                onLogout={onLogout}
            />
            {submitError && (
                <div className="qc-submit-error">
                    {submitError}
                </div>
            )}
            {submitSuccess && (
                <div className="qc-submit-success">
                    All updates submitted successfully!
                </div>
            )}
            <QCProjectViewerContent 
                projectData={projectData}
                projectStats={projectStats}
                qcCategories={qcCategories} 
                searchTerm={searchTerm} 
                sessionType={sessionType}
                onQueueUpdate={handleQueueUpdate}
                onClearUpdate={handleClearUpdate}
                queuedUpdates={queuedUpdates}
                userName={userName}
                activeFilter={activeFilter}
                selectedArea={selectedArea}
                selectedLocation={selectedLocation}
            />
        </div>
    );
};

const QCProjectLogin = ({ login }) => {
    const [ email, setEmail ] = useState('');
    const [ name, setName ] = useState('');

    const handleLogin = () => {
        login(name, email);
    };

    const handleKeyDown = (e) => {
        if(e.key === 'Enter'){
            handleLogin();
        }
    };

    return (
        <div className="qc-project-login">
            <div className="qc-login-box">
                <img src={loginLogo} className="Login-Image" alt='Login'/>
                <img src={loginText} className="Login-Image-Text" alt='Login-Text'/>
                <div className='qc-input-box'>
                    <label className='qc-input-label'>
                        Name
                    </label>
                    <input 
                        className='qc-input-field'
                        type="text" 
                        placeholder="" 
                        onKeyDown={((e) => handleKeyDown(e))}
                        onChange={(e) => setName(e.target.value)} 
                    />
                </div>
                <div className='qc-input-box'>
                    <label className='qc-input-label'>
                        Email
                    </label>
                    <input 
                        className='qc-input-field'
                        type="email" 
                        placeholder="" 
                        onKeyDown={((e) => handleKeyDown(e))}
                        onChange={(e) => setEmail(e.target.value)} 
                    />
                </div>
                <button className='qc-login-button' onClick={handleLogin}>
                    Submit
                </button>
            </div>
        </div>
    );
};

const QCViewer = () => {
    const { sessionId, projectId } = useParams();
    const [ projectData, setProjectData] = useState(null);
    const [ projectStats, setProjectStats ] = useState(null);
    const [ sessionType, setSessionType ] = useState('Inspector');
    const [ projectDataError, setProjectDataError ] = useState(null);
    const [ userEmail, setUserEmail ] = useState(null);
    const [ userName, setUserName ] = useState(null);
    const [ qcCategories, setQcCategories ] = useState(null);

    const [ searchTerm, setSearchTerm ] = useState('');



    useEffect(() => {
        if (projectData) {
            if(projectData.sessionType) setSessionType(projectData.sessionType);
        };
    },[projectData, projectStats]);

    useEffect(() => {
        setUserEmail(localStorage.getItem('userEmail'));
        setUserName(localStorage.getItem('userName'));
        getQCCategoriesList(setQcCategories, console.error);
    }, []);

    useEffect(() => {
        if(sessionId && projectId) getProjectData(sessionId, projectId, setProjectData, setProjectStats, setProjectDataError);
    }, [sessionId, projectId]);

    const login = (name, email) => {
        setUserEmail(email);
        setUserName(name);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userName', name);
    };

    const logout = () => {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        setUserEmail(null);
        setUserName(null);
    };

    const refreshProjectData = () => {
        if(sessionId && projectId) getProjectData(sessionId, projectId, setProjectData, setProjectStats, setProjectDataError);
    };

    const QCProjectViewerProps = {
        searchTerm,
        setSearchTerm,
        projectId,
        sessionType,
        projectData,
        projectStats,
        projectDataError,
        qcCategories,
        sessionId,
        userName,
        userEmail,
        refreshProjectData,
        onLogout: logout
    };

    const QCProjectLoginProps = {
        login
    };

    return (
        <div className="qc-viewer-container">
            {   userEmail && userName ?
                <QCProjectViewer {...QCProjectViewerProps}/>
                : 
                <QCProjectLogin {...QCProjectLoginProps}/> 
            }
        </div>
    );
};

export default QCViewer;